import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

const counterIcons = [
  "ri-key-2-fill",
  "ri-shield-keyhole-fill",
  "ri-user-heart-fill",
  "ri-building-2-fill",
  "ri-timer-flash-fill",
] as const;

const counterKeys = ["keys", "cylinders", "clients", "projects", "response"] as const;

function useAnimatedCounter(target: string, shouldAnimate: boolean): string {
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!shouldAnimate) {
      setDisplay("0");
      return;
    }

    const match = target.match(/^([\d,.]+)([KMB]?)\+?$/);
    if (!match) {
      setDisplay(target);
      return;
    }

    const numericPart = match[1].replace(/,/g, "");
    const suffix = match[2] || "";
    const plus = target.includes("+") ? "+" : "";

    const endValue = parseFloat(numericPart);
    let multiplier = 1;
    if (suffix === "K") multiplier = 1000;
    else if (suffix === "M") multiplier = 1000000;
    else if (suffix === "B") multiplier = 1000000000;

    const actualEnd = endValue * multiplier;
    let startTimestamp: number | null = null;
    const duration = 2200;
    let animationFrameId: number;

    const formatNumber = (val: number): string => {
      if (val >= 1000000) {
        return (val / 1000000).toFixed(1).replace(/\.0$/, "") + "M" + plus;
      }
      if (val >= 1000) {
        const divided = val / 1000;
        return (Number.isInteger(divided) ? divided.toFixed(0) : divided.toFixed(1)) + "K" + plus;
      }
      return Math.floor(val).toString() + plus;
    };

    const animate = (timestamp: number) => {
      if (startTimestamp === null) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / duration, 1);

      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * actualEnd;

      setDisplay(formatNumber(current));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [target, shouldAnimate]);

  return display;
}

function AnimatedStat({
  icon,
  value,
  label,
  index,
  visible,
}: {
  icon: string;
  value: string;
  label: string;
  index: number;
  visible: boolean;
}) {
  const displayValue = useAnimatedCounter(value, visible);

  return (
    <div
      className="flex flex-col items-center text-center p-6 md:p-8 rounded-xl bg-background-50/10 backdrop-blur-sm border border-background-50/15 hover:bg-background-50/15 transition-all duration-500 w-[calc(50%-0.375rem)] sm:w-[calc(33.333%-0.75rem)] lg:w-[calc(20%-1rem)]"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease-out ${index * 0.15}s, transform 0.6s ease-out ${index * 0.15}s`,
      }}
    >
      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-accent-500/20 flex items-center justify-center mb-4">
        <i className={`${icon} text-accent-400 text-xl md:text-2xl`}></i>
      </div>
      <span className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 tracking-tight whitespace-nowrap">
        {displayValue}
      </span>
      <span className="text-background-50/60 text-xs md:text-sm font-medium mt-2 uppercase tracking-widest">
        {label}
      </span>
    </div>
  );
}

export default function TrustSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setVisible(true);
      }
    });
  }, []);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.3,
      rootMargin: "0px 0px -60px 0px",
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [handleIntersect]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-background-950">
      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-6 py-16 md:py-20 lg:py-24">
        <div className="text-center mb-10 md:mb-14 lg:mb-16">
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-accent-500"></span>
            <span className="text-xs md:text-sm font-medium text-accent-400 uppercase tracking-wider">
              {t("trust.label")}
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 tracking-tight">
            {t("trust.heading")}
          </h2>
          <p className="text-sm md:text-base text-background-50/60 mt-4 md:mt-5 max-w-lg mx-auto leading-relaxed">
            {t("trust.text")}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 md:gap-5">
          {counterKeys.map((key, index) => (
            <AnimatedStat
              key={key}
              icon={counterIcons[index]}
              value={t(`trust.counters.${key}.value`)}
              label={t(`trust.counters.${key}.label`)}
              index={index}
              visible={visible}
            />
          ))}
        </div>

        <p className="text-center text-background-50/30 text-xs mt-8 md:mt-10">
          {t("brand")} &mdash; {t("about.stats.experience")}
        </p>
      </div>
    </section>
  );
}